import type { EventLog } from '@geartracker/types';

import axios, { endpoints } from 'src/utils/axios';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

export const eventKeys = {
  all: ['events'] as const,
  list: (skip?: number, take?: number) =>
    [...eventKeys.all, 'list', skip, take] as const,
  page: (page: number, pageSize: number) =>
    [...eventKeys.all, 'page', page, pageSize] as const,
  userPage: (userId: number, page: number, pageSize: number) =>
    [...eventKeys.all, 'user', userId, page, pageSize] as const,
};

export const useEvents = (skip = 0, take = 100) => {
  return useQuery({
    queryKey: eventKeys.list(skip, take),
    queryFn: async (): Promise<EventLog[]> => {
      const response = await axios.get(endpoints.events.root, {
        params: { skip, take },
      });
      return response.data;
    },
  });
};

export const useEventPage = (
  page: number,
  pageSize: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: eventKeys.page(page, pageSize),
    queryFn: async (): Promise<{ items: EventLog[]; total: number | null }> => {
      const response = await axios.get(endpoints.events.root, {
        params: { skip: page * pageSize, take: pageSize },
      });
      return response.data;
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useUserEventPage = (
  userId: number,
  page: number,
  pageSize: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: eventKeys.userPage(userId, page, pageSize),
    queryFn: async (): Promise<{ items: EventLog[]; total: number | null }> => {
      const response = await axios.get(endpoints.events.user(userId), {
        params: { skip: page * pageSize, take: pageSize },
      });
      return response.data;
    },
    enabled: enabled && !!userId,
    placeholderData: keepPreviousData,
  });
};
